import React, { useEffect, useState } from "react";
import api, { fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { FileText, ExternalLink, Download } from "lucide-react";
import { toast } from "sonner";

export const LETTER_ROLES = ["SUPER_ADMIN", "TEMPLE_ADMIN", "SUPERVISOR"];

// Recommendation letter (PDF / image) shown inside the page. Browsers block opening data: links
// directly, so the file is turned into a blob URL for the viewer, new tab and download.
export default function LetterViewer({ txnId, autoLoad = false, path = "", label = "Recommendation Letter", roles = LETTER_ROLES }) {
  const { user } = useAuth();
  const [letter, setLetter] = useState(null);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(autoLoad);

  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);
  useEffect(() => { setLetter(null); setUrl(""); if (autoLoad) load(); }, [txnId, path]); // eslint-disable-line

  async function load() {
    setBusy(true);
    try {
      const r = await api.get(path || `/protocol/${encodeURIComponent(txnId)}/letter`);
      const blob = await (await fetch(r.data.data)).blob();
      setUrl(URL.createObjectURL(blob));
      setLetter(r.data);
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  }

  if (!roles.includes(user?.role)) return null;

  if (!letter && autoLoad) return <p className="text-sm text-slate-500 py-10 text-center">{busy ? `Loading ${label.toLowerCase()}…` : `${label} could not be loaded.`}</p>;
  if (!letter) {
    return (
      <Button type="button" variant="outline" size="sm" onClick={load} disabled={busy} data-testid="letter-view-btn">
        <FileText className="w-4 h-4 mr-1" /> {busy ? `Loading ${label.toLowerCase()}…` : `View ${label}`}
      </Button>
    );
  }
  const isPdf = /^data:application\/pdf/i.test(letter.data);
  return (
    <div className="space-y-2" data-testid="letter-viewer">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-gold-700 font-bold uppercase flex items-center gap-1"><FileText className="w-4 h-4" /> {label} · <span className="normal-case font-normal text-slate-600">{letter.name}</span></p>
        <div className="flex gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => window.open(url, "_blank")}><ExternalLink className="w-4 h-4 mr-1" /> Open</Button>
          <a href={url} download={letter.name || "letter"}><Button type="button" variant="outline" size="sm"><Download className="w-4 h-4 mr-1" /> Download</Button></a>
        </div>
      </div>
      {isPdf
        ? <iframe title={label} src={url} className="w-full h-[65vh] border border-[#E5D9C5] rounded" />
        : <img src={url} alt={label} className="max-h-[65vh] mx-auto border border-[#E5D9C5] rounded" />}
    </div>
  );
}
